import * as React from "react";
import { useMediaLibrary, type MediaItem } from "@/hooks/use-media";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Search, X, Image as ImageIcon, Grid, List } from "lucide-react";

interface MediaPickerProps {
  isOpen: boolean;
  onClose: () => void;
  onSelect: (url: string) => void;
  fileType?: "image" | "video" | "all";
  multiple?: boolean;
  onSelectMultiple?: (urls: string[]) => void;
}

export function MediaPicker({
  isOpen,
  onClose,
  onSelect,
  fileType = "image",
  multiple = false,
  onSelectMultiple,
}: MediaPickerProps) {
  const [page, setPage] = React.useState(1);
  const [searchQuery, setSearchQuery] = React.useState("");
  const [viewMode, setViewMode] = React.useState<"grid" | "list">("grid");
  const [selectedItems, setSelectedItems] = React.useState<string[]>([]);

  const { data: mediaData, isLoading } = useMediaLibrary({
    page,
    pageSize: 24,
    fileType: fileType === "all" ? undefined : fileType,
    search: searchQuery || undefined,
    sortBy: "uploaded_at",
    sortDir: "desc",
  });

  const handleSelect = (item: MediaItem) => {
    if (multiple && onSelectMultiple) {
      if (selectedItems.includes(item.url)) {
        setSelectedItems(selectedItems.filter((url) => url !== item.url));
      } else {
        setSelectedItems([...selectedItems, item.url]);
      }
    } else {
      onSelect(item.url);
      onClose();
    }
  };

  const handleConfirmMultiple = () => {
    if (onSelectMultiple && selectedItems.length > 0) {
      onSelectMultiple(selectedItems);
      setSelectedItems([]);
      onClose();
    }
  };

  const handleClose = () => {
    setSelectedItems([]);
    setSearchQuery("");
    setPage(1);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-xl shadow-xl max-w-6xl w-full max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="p-6 border-b border-gray-200 flex items-center justify-between">
          <div>
            <h3 className="text-lg font-semibold text-gray-900">
              {multiple ? "Chọn nhiều hình ảnh" : "Chọn hình ảnh"}
            </h3>
            <p className="text-sm text-gray-500 mt-1">
              Chọn từ media library hoặc tìm kiếm
            </p>
          </div>
          <button
            onClick={handleClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Search and Controls */}
        <div className="p-6 border-b border-gray-200">
          <div className="flex items-center space-x-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
              <Input
                type="text"
                placeholder="Tìm kiếm hình ảnh..."
                value={searchQuery}
                onChange={(e) => {
                  setSearchQuery(e.target.value);
                  setPage(1);
                }}
                className="pl-10"
              />
            </div>
            <div className="flex items-center space-x-2">
              <button
                onClick={() => setViewMode("grid")}
                className={`p-2 border rounded-lg transition-colors ${
                  viewMode === "grid"
                    ? "border-primary bg-primary/5 text-primary"
                    : "border-gray-300 text-gray-600 hover:border-primary"
                }`}
              >
                <Grid className="h-4 w-4" />
              </button>
              <button
                onClick={() => setViewMode("list")}
                className={`p-2 border rounded-lg transition-colors ${
                  viewMode === "list"
                    ? "border-primary bg-primary/5 text-primary"
                    : "border-gray-300 text-gray-600 hover:border-primary"
                }`}
              >
                <List className="h-4 w-4" />
              </button>
            </div>
          </div>
          {multiple && selectedItems.length > 0 && (
            <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
              <p className="text-sm text-blue-700">
                Đã chọn {selectedItems.length} hình ảnh
              </p>
            </div>
          )}
        </div>

        {/* Media Grid */}
        <div className="flex-1 overflow-auto p-6">
          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
            </div>
          ) : mediaData?.media && mediaData.media.length === 0 ? (
            <div className="text-center py-12">
              <ImageIcon className="h-12 w-12 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-500">Không tìm thấy hình ảnh nào</p>
            </div>
          ) : (
            <>
              {viewMode === "grid" ? (
                <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
                  {mediaData?.media.map((item: MediaItem) => (
                    <MediaCard
                      key={item.id}
                      item={item}
                      isSelected={selectedItems.includes(item.url)}
                      onClick={() => handleSelect(item)}
                    />
                  ))}
                </div>
              ) : (
                <div className="space-y-2">
                  {mediaData?.media.map((item: MediaItem) => (
                    <MediaListItem
                      key={item.id}
                      item={item}
                      isSelected={selectedItems.includes(item.url)}
                      onClick={() => handleSelect(item)}
                    />
                  ))}
                </div>
              )}

              {/* Pagination */}
              {mediaData && mediaData.pagination.totalPages > 1 && (
                <div className="mt-8 flex items-center justify-between">
                  <p className="text-sm text-gray-500">
                    Trang {page} / {mediaData.pagination.totalPages}
                  </p>
                  <div className="flex items-center space-x-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setPage((p) => Math.max(1, p - 1))}
                      disabled={page === 1}
                    >
                      Trước
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() =>
                        setPage((p) =>
                          Math.min(mediaData.pagination.totalPages, p + 1)
                        )
                      }
                      disabled={page === mediaData.pagination.totalPages}
                    >
                      Sau
                    </Button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>

        {/* Footer */}
        {multiple && (
          <div className="p-6 border-t border-gray-200 flex items-center justify-end space-x-3">
            <Button variant="outline" onClick={handleClose}>
              Hủy
            </Button>
            <Button
              variant="yellow"
              onClick={handleConfirmMultiple}
              disabled={selectedItems.length === 0}
            >
              Chọn {selectedItems.length > 0 && `(${selectedItems.length})`}
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}

function MediaCard({
  item,
  isSelected,
  onClick,
}: {
  item: MediaItem;
  isSelected: boolean;
  onClick: () => void;
}) {
  return (
    <div
      className={`group relative bg-gray-100 rounded-lg overflow-hidden cursor-pointer border-2 transition-all ${
        isSelected
          ? "border-primary ring-2 ring-primary ring-offset-2"
          : "border-transparent hover:border-gray-300"
      }`}
      onClick={onClick}
    >
      <div className="aspect-square">
        {item.fileType === "image" && item.url ? (
          <img
            src={item.url}
            alt={item.altText || item.originalFilename}
            className="w-full h-full object-cover"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center bg-gray-100">
            <ImageIcon className="h-12 w-12 text-gray-400" />
          </div>
        )}
      </div>
      {isSelected && (
        <div className="absolute inset-0 bg-primary/20 flex items-center justify-center">
          <div className="w-8 h-8 bg-primary rounded-full flex items-center justify-center">
            <svg
              className="w-5 h-5 text-white"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M5 13l4 4L19 7"
              />
            </svg>
          </div>
        </div>
      )}
      <div className="absolute bottom-0 left-0 right-0 bg-black/50 text-white p-2">
        <p className="text-xs truncate">{item.originalFilename}</p>
      </div>
    </div>
  );
}

function MediaListItem({
  item,
  isSelected,
  onClick,
}: {
  item: MediaItem;
  isSelected: boolean;
  onClick: () => void;
}) {
  return (
    <div
      className={`flex items-center space-x-4 p-4 border rounded-lg cursor-pointer transition-all ${
        isSelected
          ? "border-primary bg-primary/5"
          : "border-gray-200 hover:bg-gray-50"
      }`}
      onClick={onClick}
    >
      <div className="w-16 h-16 flex-shrink-0">
        {item.fileType === "image" && item.url ? (
          <img
            src={item.url}
            alt={item.altText || item.originalFilename}
            className="w-full h-full object-cover rounded"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center bg-gray-100 rounded">
            <ImageIcon className="h-8 w-8 text-gray-400" />
          </div>
        )}
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium text-gray-900 truncate">
          {item.originalFilename}
        </p>
        <p className="text-xs text-gray-500">{item.fileType}</p>
      </div>
      {isSelected && (
        <div className="w-6 h-6 bg-primary rounded-full flex items-center justify-center">
          <svg
            className="w-4 h-4 text-white"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M5 13l4 4L19 7"
            />
          </svg>
        </div>
      )}
    </div>
  );
}















